/**
 * Admin-on-behalf client-group tag for a managed user's broker account.
 *
 * Backend: PUT /admin/managed-users/{user}/broker-accounts/{client}/group
 *   body: {group_id: <uuid> | null}
 *   - Writes broker-accounts.group_id (SET or REMOVE based on null/value)
 *   - Validates the group_id against client-groups (FK check)
 *   - Emits `group_set_on_behalf` audit log with before/after group_ids
 *
 * UI: simple dropdown of groups + a "— No group —" option to clear. Loads the
 * list via clientGroupsService.listForFilter (Cognito-only projection, AMs OK).
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import managedBrokerService from '../../services/managedBrokerService';
import clientGroupsService, { ClientGroupOption } from '../../services/clientGroupsService';

interface EditGroupDialogProps {
  open: boolean;
  managedUserId: string;
  managedClientId: string;
  managedDisplayName: string;
  /** Current group_id on the broker-accounts row (null/empty if untagged). */
  currentGroupId: string | null;
  /** Current group name (for display in the prompt). Optional. */
  currentGroupName?: string;
  onClose: () => void;
  onSuccess: (newGroupId: string | null, newGroupName: string) => void;
}

const NO_GROUP = '__none__';   // sentinel for the dropdown's "no group" option

const EditGroupDialog: React.FC<EditGroupDialogProps> = ({
  open,
  managedUserId,
  managedClientId,
  managedDisplayName,
  currentGroupId,
  currentGroupName,
  onClose,
  onSuccess,
}) => {
  const [groups, setGroups] = useState<ClientGroupOption[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [selected, setSelected] = useState<string>(currentGroupId || NO_GROUP);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(currentGroupId || NO_GROUP);
    setError(null);
    setIsLoadingGroups(true);
    clientGroupsService.listForFilter()
      .then(setGroups)
      .catch((e) => setError(e?.message || 'Failed to load groups'))
      .finally(() => setIsLoadingGroups(false));
  }, [open, currentGroupId]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const newGroupId = selected === NO_GROUP ? null : selected;
    // Guard: matches the existing value — no-op.
    if ((currentGroupId || null) === newGroupId) {
      setError('Selection matches the current value — nothing to update');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await managedBrokerService.setGroupOnBehalf(
        managedUserId, managedClientId, newGroupId,
      );
      const newName = newGroupId
        ? (groups.find((g) => g.group_id === newGroupId)?.name || '')
        : '';
      onSuccess(result.group_id, newName);
    } catch (err: any) {
      setError(err.message || 'Failed to update group');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={handleClose} maxWidth="md" ariaLabel="Set Client Group">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Set Client Group</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              On behalf of <span className="font-medium">{managedDisplayName}</span> ({managedClientId})
            </p>
            {currentGroupName && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Current: <span className="font-medium">{currentGroupName}</span>
              </p>
            )}
          </div>
          <button onClick={handleClose} disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="client-group-select"
                   className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Group
            </label>
            <select
              id="client-group-select"
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setError(null); }}
              disabled={isSubmitting || isLoadingGroups}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={NO_GROUP}>— No group —</option>
              {groups.map((g) => (
                <option key={g.group_id} value={g.group_id}>
                  {g.name}
                </option>
              ))}
            </select>
            {isLoadingGroups && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading groups…</p>
            )}
            {!isLoadingGroups && groups.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                No groups defined yet. A SuperAdmin can create one on the Managed User Assignments page.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting}
                  disabled={isSubmitting || isLoadingGroups}>
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditGroupDialog;
